import React, { useEffect, useState, useCallback } from 'react';
import { Paperclip, Plus, Camera, Eye, Trash2, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DocumentUploader } from './DocumentUploader';
import { PhotoCaptureModal } from './PhotoCaptureModal';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { documentService } from '@/services/documentService';
import { DocumentRecord, DocumentType, EntityType } from '@/types/database';
import { formatDate, formatFileSize } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface DocumentAttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  entityTitle?: string;
  defaultDocumentType?: DocumentType;
}

export const DocumentAttachmentModal: React.FC<DocumentAttachmentModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityTitle = 'Record Attachments',
  defaultDocumentType = 'weighing_sheet',
}) => {
  const { isOperations } = useAuth();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Sub-modal states
  const [showUploadForm, setShowUploadForm] = useState<boolean>(false);
  const [showCameraModal, setShowCameraModal] = useState<boolean>(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [voidDoc, setVoidDoc] = useState<DocumentRecord | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const docs = await documentService.getEntityDocuments(entityType, entityId);
      setDocuments(docs);
    } catch (err: any) {
      console.error('[DocumentAttachmentModal] Error loading documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      setShowUploadForm(false);
    }
  }, [isOpen, loadDocuments]);

  const handleSoftVoid = async (reason: string) => {
    if (!voidDoc) return;
    try {
      await documentService.softVoidDocument(voidDoc.id, reason);
      setVoidDoc(null);
      await loadDocuments();
    } catch (err: any) {
      console.error('[DocumentAttachmentModal] Void error:', err);
      setError(err.message || 'Failed to soft-void document');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <div className="flex items-center gap-2 text-slate-900">
                <Paperclip className="w-5 h-5 text-emerald-700" />
                <DialogTitle>{entityTitle}</DialogTitle>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Attached weighing sheets, DR invoices, receipts, and lab diagnostic certificates.
            </p>
          </DialogHeader>

          {/* Action Bar (Upload / Camera) */}
          {isOperations && (
            <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200 my-2">
              <span className="text-xs font-semibold text-slate-700">Add Attachment:</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={showUploadForm ? 'secondary' : 'outline'}
                  onClick={() => setShowUploadForm(!showUploadForm)}
                  className="text-xs h-8"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> {showUploadForm ? 'Hide Uploader' : 'Upload File'}
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => setShowCameraModal(true)}
                  className="text-xs h-8 bg-emerald-700 hover:bg-emerald-600"
                >
                  <Camera className="w-3.5 h-3.5 mr-1" /> Mobile Camera
                </Button>
              </div>
            </div>
          )}

          {/* Collapsible Uploader */}
          {showUploadForm && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-2">
              <DocumentUploader
                entityType={entityType}
                entityId={entityId}
                defaultDocumentType={defaultDocumentType}
                onUploadSuccess={() => {
                  setShowUploadForm(false);
                  loadDocuments();
                }}
              />
            </div>
          )}

          {/* Documents Table / List */}
          <div className="flex-1 overflow-y-auto min-h-[200px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center p-8 text-slate-400 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
                <p className="text-xs">Loading attachments...</p>
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-slate-400">
                <FileText className="w-10 h-10 mb-2 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">No documents attached yet</p>
                <p className="text-xs text-slate-500 mt-1">Upload weighing scale slips, receipts, or photos to link them to this record.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center gap-3 truncate">
                      <div className="w-8 h-8 rounded bg-emerald-50 text-emerald-800 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="truncate text-left">
                        <p className="text-sm font-medium text-slate-900 truncate">{doc.file_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="font-semibold text-slate-600">
                            {doc.document_type.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <span>•</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>{formatDate(doc.created_at)}</span>
                        </div>
                        {doc.notes && (
                          <p className="text-xs text-slate-600 italic truncate mt-0.5">{doc.notes}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewDoc(doc)}
                        className="h-8 text-xs px-2.5"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      {isOperations && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setVoidDoc(doc)}
                          className="h-8 text-xs px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Soft-void document"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modals */}
      <PhotoCaptureModal
        isOpen={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        entityType={entityType}
        entityId={entityId}
        defaultDocumentType={defaultDocumentType}
        onPhotoCaptured={() => {
          setShowCameraModal(false);
          loadDocuments();
        }}
      />

      <DocumentPreviewModal
        isOpen={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />

      <SoftVoidConfirmModal
        isOpen={voidDoc !== null}
        onClose={() => setVoidDoc(null)}
        onConfirm={handleSoftVoid}
        recordIdentifier={voidDoc?.file_name || 'Document'}
        title="Soft-Void Document Attachment"
        description="The document metadata will be flagged as voided and excluded from active views. The physical file remains securely preserved in the storage audit trail."
      />
    </>
  );
};
